import React, {useCallback, useEffect} from 'react';
import {browserHistory, Link} from 'react-router';
import {lightTheme} from '@components/@theme/lightTheme';
import Pagination from '@components/pagination';
import {List, Map} from 'immutable';
import Loading from 'components/pure/utils/Loading';
import Team from 'records/Team';
import Department from 'records/Department';
import SortableTableHeader from 'components/pure/sortable-tables/SortableTableHeader';
import {SortDirection, toggleSort} from 'utils/const';
import {UserAdminSortFields, UserTableView} from '@store/@types/UserAdmin';
import AdminUserActions from '@store/admin/AdminUserActions';
import {TeamId} from 'records/TeamId';
import DisplayName from 'components/pure/admin/DisplayName';
import TeamCell from 'components/pure/admin/TeamCell';
import formatDate from 'utils/formatDate';
import {PhoneIcon} from '@components/icon/PhoneIcon';
import CheckIcon from '@material-ui/icons/Check';
import 'styles/admin/AdminUsers.styl';
import User from 'records/User';
import * as queryParams from 'utils/queryParams';
import {UserAdminToolbar} from '@components/admin/UserAdminToolbar';
import throttle from 'lodash/throttle';
import {UserTableQuery, QueryParams} from '@components/admin/UserTableQueryParams';
import debounce from 'lodash/debounce';
import {ActiveCell} from '@components/admin/ActiveCell';
import cn from 'classnames';
import Button from '@material-ui/core/Button';
import {AdminUserSource} from '@store/admin/AdminUserSource';
import {featureFlags} from 'utils/const';
import hasFlag from 'utils/hasFlag';

const HeaderLabel = {
  [UserAdminSortFields.DISPLAY_NAME]: 'Display Name',
  [UserAdminSortFields.TEAM_NAME]: 'Team',
  [UserAdminSortFields.EMAIL]: 'Email',
  [UserAdminSortFields.LAST_LOGIN]: 'Last Login',
  [UserAdminSortFields.ACTIVE]: 'Login Enabled',
  [UserAdminSortFields.SLACK_ENABLED]: 'Slack Enabled',
  [UserAdminSortFields.RECORD_SCHEDULED_CALLS]: 'Record Scheduled Calls',
  [UserAdminSortFields.CALENDAR_CONNECTED]: 'Calendar Connected',
  [UserAdminSortFields.HAS_CALLS]: 'Calls in last 14 days',
};
const HeaderTooltip = {
  [UserAdminSortFields.HAS_CALLS]: 'User has had a call recorded within the last 14 days',
  [UserAdminSortFields.SLACK_ENABLED]: 'User’s Slack is connected',
  [UserAdminSortFields.ACTIVE]: 'User is enabled to log in',
  [UserAdminSortFields.CALENDAR_CONNECTED]: 'User’s calendar connection is active',
  [UserAdminSortFields.RECORD_SCHEDULED_CALLS]:
    'Calls on calendar will be joined by a bot automatically',
};

const ITEMS_PER_PAGE = 20;

interface AdminUserListProps {
  users: List<UserTableView>;
  usersLoading: boolean;
  teams: Map<TeamId, Team>;
  // Raw query params
  query: QueryParams<UserTableQuery>;
  departmentProfiles: List<Department>;
  showSlack: boolean;
  totalResults: number;
  hasSlackLoaded: boolean;
  tenantId?: string;
}

function parsePage(num?: string) {
  try {
    return parseInt(num!);
  } catch {
    // page is not set or is invalid, set query to 1
    queryParams.set({page: 1});
    return 1;
  }
}

const debouncedSearch = debounce(AdminUserActions.fetchUserTable, 1000);

export const AdminUserList: React.FC<AdminUserListProps> = ({
  query,
  users,
  usersLoading,
  teams,
  showSlack,
  departmentProfiles,
  totalResults,
  hasSlackLoaded,
  tenantId,
}) => {
  const {
    sort = UserAdminSortFields.DISPLAY_NAME,
    sortDirection = SortDirection.ASC,
    page = '1',
    showArchived,
    teamId,
    departmentId,
    searchText,
  } = query;
  const updateQuery = (params: UserTableQuery) => {
    browserHistory.replace({
      pathname: location.pathname,
      query: {
        ...query,
        ...params,
        // Reset page to 1 when changing search params
        page: params.page ? params.page : 1,
      },
    });
  };

  const showRecordScheduledCalls =
    hasFlag(featureFlags.soneroBotEnabled) || hasFlag(featureFlags.recallBotEnabled);
  const showCalConnected = !hasFlag(featureFlags.hasIntelligenceCalendar);

  const pageNum = parsePage(page);
  const runSearch = useCallback(() => {
    debouncedSearch({
      sort: sort as UserAdminSortFields,
      sortDirection: sortDirection as SortDirection,
      departmentId,
      teamId,
      offset: ITEMS_PER_PAGE * (pageNum - 1),
      search: searchText,
      includeInactive: showArchived === 'true',
    });
  }, [pageNum, sort, sortDirection, departmentId, teamId, showArchived, searchText]);
  useEffect(() => runSearch(), [runSearch]);

  const userRows = users.map(row => (
    <tr key={row.userId}>
      <td>
        <DisplayName user={new User({id: row.userId, ...row})} />
      </td>
      <td className="team_name">
        <TeamCell
          onChange={async teamId => {
            await AdminUserActions.setUserTeam({
              teamId,
              userId: row.userId,
            });
            runSearch();
          }}
          userId={row.userId}
          teamId={row.teamId}
          teams={teams}
          active={row.teamActive}
          teamName={row.teamName}
        />
      </td>
      <td>{row.email}</td>
      <td className="last_login">{formatDate(row.lastLogin)}</td>
      <td className="active">
        <ActiveCell
          active={row.active}
          allowToggle={!tenantId}
          onChange={throttle(async () => {
            await AdminUserActions.setUserActive({
              userId: row.userId,
              active: !row.active,
            });
            runSearch();
          }, 1000)}
        />
      </td>
      {showSlack && (
        <td className="slack_enabled">
          {row.slackEnabled ? <CheckIcon className="active admin-users-cell" /> : null}
        </td>
      )}
      {showRecordScheduledCalls && (
        <td className="record_scheduled_calls">
          {!row.botAutoJoinExclusion ? <CheckIcon className="active admin-users-cell" /> : null}
        </td>
      )}
      {showCalConnected && (
        <td className="calendar_connected">
          {
            <div>
              <CheckIcon
                className={cn(
                  row.calendarConnected ? 'active' : 'inactive',
                  'admin-users-cell',
                  'disabled'
                )}
              />
            </div>
          }
        </td>
      )}
      <td className="has_calls">
        <PhoneIcon
          style={{
            fill: row.hasCalls ? lightTheme.palette.primary.main : lightTheme.palette.icon.grey,
          }}
          tooltip={row.hasCalls ? 'Had calls in the past 14 days' : 'No calls in the past 14 days'}
        />
      </td>
    </tr>
  ));

  return (
    <div className="admin-users">
      <div className="page-header">
        <h1>Manage Users</h1>
        <Button
          component={Link}
          to={
            !!tenantId
              ? `https://login.mediafly.com/r/revenue360/${tenantId}/users/create`
              : '/admin/users/new'
          }
          target={!!tenantId ? '_blank' : ''}
          variant="contained"
          color="primary"
          className="ev-button">
          New User
        </Button>
      </div>
      <UserAdminToolbar
        departmentProfiles={departmentProfiles}
        selectedDepartment={departmentId}
        showArchived={showArchived === 'true'}
        searchText={searchText ?? ''}
        users={users}
        updateQuery={updateQuery}
        runSearch={() =>
          AdminUserSource.fetchUserTable({
            sort: sort as UserAdminSortFields,
            sortDirection: sortDirection as SortDirection,
            departmentId,
            teamId,
            search: searchText,
            includeInactive: showArchived === 'true',
            offset: 0,
            limit: 10_000,
          }).then(r => r.users)
        }
      />
      {hasSlackLoaded ? (
        <div className="table-container">
          <table className="user-list-table">
            <thead>
              <tr>
                {Object.values(UserAdminSortFields).map(header => {
                  if (header === UserAdminSortFields.SLACK_ENABLED && !showSlack) {
                    return null;
                  }
                  if (
                    header === UserAdminSortFields.RECORD_SCHEDULED_CALLS &&
                    !showRecordScheduledCalls
                  ) {
                    return null;
                  }
                  if (header === UserAdminSortFields.CALENDAR_CONNECTED && !showCalConnected) {
                    return null;
                  }
                  return (
                    <th key={header} className={`${header} admin-users-header`}>
                      <SortableTableHeader
                        label={HeaderLabel[header]}
                        renderColumn={header}
                        onClick={() => {
                          if (sort === header) {
                            updateQuery({
                              sortDirection: toggleSort(sortDirection as SortDirection),
                            });
                          } else {
                            updateQuery({sort: header});
                          }
                        }}
                        sortDirection={sortDirection}
                        sortColumn={sort}
                        popoverMessage={HeaderTooltip[header]}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>{!usersLoading && userRows}</tbody>
          </table>
          {usersLoading ? (
            <div className="loading-state">
              <Loading />
            </div>
          ) : users.isEmpty() ? (
            <div className="empty-state">
              No {showArchived === 'true' ? '' : 'active '}users were found matching the search
            </div>
          ) : null}
        </div>
      ) : (
        <Loading />
      )}
      <div className="pagination-wrapper">
        <Pagination
          count={Math.ceil(totalResults / ITEMS_PER_PAGE)}
          page={pageNum}
          onChange={(e, page) => updateQuery({page})}
        />
      </div>
    </div>
  );
};
